// Bilingual EN/FR copy. Keep keys short and grouped per page/section.
export const translations = {
  en: {
    nav: {
      home: "Home",
      about: "About",
      products: "Products",
      industries: "Industries",
      sustainability: "Sustainability",
      news: "News",
      careers: "Careers",
      contact: "Contact",
      getQuote: "Get a Quote",
    },
    common: {
      learnMore: "Learn more",
      exploreProducts: "Explore products",
      contactUs: "Contact us",
      readMore: "Read more",
      viewAll: "View all",
      send: "Send message",
      sending: "Sending...",
    },
    home: {
      heroOverline: "Industrial Chemicals · Mining Solutions",
      heroTitle: "Powering Africa's industrial growth with innovative and sustainable chemistry.",
      heroSub:
        "Masika Chem SARL supplies key chemicals and spare parts to the mining industry across the Greater Katanga region — the right product, at the right price, at the right time.",
      ctaPrimary: "Explore our products",
      ctaSecondary: "Talk to our team",
      trustedBy: "Trusted by leading mining operators across the DRC",
      stat1: "30+", stat1Label: "Specialty chemicals",
      stat2: "8+", stat2Label: "Major mine partners",
      stat3: "24/7", stat3Label: "Logistics & support",
      stat4: "100%", stat4Label: "ARSP compliant",
      industriesOverline: "Industries we serve",
      industriesTitle: "Engineered chemistry for every stage of mineral processing.",
      industriesSub:
        "From comminution to refining, our reagents and consumables keep production lines running across copper, cobalt, gold, and industrial water treatment operations.",
      copper: "Copper",
      copperDesc: "Reagents and grinding media tuned for sulphide and oxide copper circuits.",
      cobalt: "Cobalt",
      cobaltDesc: "Precision flotation and leaching chemistry for high-grade cobalt recovery.",
      gold: "Gold",
      goldDesc: "Cyanidation, carbon-in-pulp and refining chemicals for gold operations.",
      water: "Industrial water",
      waterDesc: "Flocculants, coagulants and pH chemistry for process and effluent water.",
      productsOverline: "Product catalog",
      productsTitle: "Six categories. One trusted supply chain.",
      productsSub:
        "We hold a comprehensive portfolio across the entire mineral processing value chain. Specifications, datasheets and packaging on request.",
      ctaBlockTitle: "Ready to optimize your processing circuit?",
      ctaBlockSub:
        "Speak with our Lubumbashi team about reagent supply, technical support and after-services tailored to your operation.",
      ctaBlockBtn: "Request a consultation",
    },
    about: {
      overline: "About us",
      title: "A Katanga-based partner for world-class mining operations.",
      sub:
        "Masika Chem SARL was built to keep production running. We combine local presence in Lubumbashi with a disciplined supply chain that delivers ARSP-compliant chemicals and spare parts to the operators driving Africa's industrial growth.",
      missionTitle: "Our mission",
      missionBody:
        "Providing innovative and sustainable solutions powering Africa's industrial growth — by delivering the right product, at the right price, at the right time.",
      visionTitle: "Our vision",
      visionBody:
        "To be the most reliable industrial chemicals partner for the African mining sector, recognised for technical depth, integrity and uninterrupted supply.",
      valuesTitle: "What we stand for",
      v1: "Reliability",
      v1d: "Continuous supply so our customers' production never stops.",
      v2: "Quality",
      v2d: "Certified reagents and consumables that meet ARSP and operator specifications.",
      v3: "Local expertise",
      v3d: "Deep knowledge of Katanga mining operations, logistics and regulations.",
      v4: "Sustainability",
      v4d: "Responsible chemistry that supports long-term industrial development.",
    },
    products: {
      overline: "Product catalog",
      title: "Industrial chemicals & spare parts for mining.",
      sub:
        "Organized by stage of the mineral processing circuit. Custom packaging and bulk supply available. Contact our team for technical datasheets and delivery schedules.",
      tableProduct: "Product",
      tableFormula: "Formula / Grade",
      tableApp: "Primary application",
      tableCta: "Quote",
      categories: {
        size_reduction: "Size reduction",
        separation: "Separation",
        flotation: "Flotation",
        dewatering: "Dewatering",
        refining: "Refining",
        spares: "Grinding media & spares",
      },
    },
    industries: {
      overline: "Industries",
      title: "Built around the realities of African mining.",
      sub:
        "Every mineral has its chemistry. We support copper, cobalt, gold and water-treatment operations with reagent programs designed for local ore bodies and operating conditions.",
    },
    sustainability: {
      overline: "Sustainability & HSE",
      title: "Safe chemistry. Responsible supply.",
      sub:
        "Health, safety and environmental stewardship are at the core of how we source, transport and deliver chemicals across the Copperbelt.",
      p1Title: "Health & Safety",
      p1Body:
        "We comply with international handling and transport standards for hazardous materials, with SDS provided for every product line.",
      p2Title: "Environment",
      p2Body:
        "We work with operators to right-size reagent dosing — reducing chemical footprint without compromising recovery.",
      p3Title: "Governance",
      p3Body:
        "ARSP-compliant procurement and traceable supply chains from manufacturer to mine gate.",
    },
    news: {
      overline: "News & insights",
      title: "Notes from the Copperbelt.",
      sub: "Industry updates, technical notes and announcements from the Masika Chem team.",
      items: [
        {
          tag: "Operations",
          title: "Expanded warehousing in Lubumbashi to support 24/7 reagent supply",
          excerpt: "New storage capacity enables faster turnaround for sulphuric acid, xanthates and grinding media across major Katanga operations.",
          date: "Nov 2025",
        },
        {
          tag: "Technical",
          title: "Optimizing collector dosage in oxide copper flotation circuits",
          excerpt: "A short technical note on tuning PAX/SIBX blends for mixed sulphide-oxide ore bodies common across the DRC.",
          date: "Oct 2025",
        },
        {
          tag: "Partnership",
          title: "Renewed multi-year supply agreement with a major Katanga producer",
          excerpt: "Masika Chem extends its long-term reagent and spare parts supply partnership with one of the region's top copper-cobalt operators.",
          date: "Sep 2025",
        },
      ],
    },
    careers: {
      overline: "Careers",
      title: "Build the industrial backbone of Africa with us.",
      sub:
        "We're a lean, ambitious team headquartered in Lubumbashi. If you care about reliability, chemistry and operational excellence, we'd love to meet you.",
      openTitle: "Current openings",
      none: "We're not actively hiring right now, but we always welcome strong CVs.",
      role1: "Sales Engineer — Mining Reagents",
      role1Loc: "Lubumbashi, DRC",
      role2: "Logistics Coordinator",
      role2Loc: "Lubumbashi, DRC",
      role3: "Technical Sales Representative — Kolwezi",
      role3Loc: "Kolwezi, DRC",
      apply: "Apply via email",
    },
    contact: {
      overline: "Contact",
      title: "Talk to our Lubumbashi team.",
      sub:
        "Reach out for product specifications, quotations or technical support. Our team typically replies within one business day.",
      formName: "Full name",
      formCompany: "Company",
      formEmail: "Work email",
      formPhone: "Phone (optional)",
      formMessage: "How can we help?",
      formSubmit: "Send message",
      success: "Thank you. Your message has been received — we'll be in touch shortly.",
      error: "Something went wrong. Please try again or email us directly.",
      address: "Address",
      addressValue: "No. 44 Avenue Sendwe, Lubumbashi, D.R. Congo",
      phone: "Phone",
      email: "Email",
      hours: "Office hours",
      hoursValue: "Monday – Friday · 08:00 – 17:00 (CAT)",
    },
    footer: {
      tagline:
        "Industrial chemicals and spare parts for mining operations across the Greater Katanga region.",
      navTitle: "Navigate",
      contactTitle: "Contact",
      legalTitle: "Legal",
      privacy: "Privacy policy",
      terms: "Terms of use",
      rights: "All rights reserved.",
    },
  },
  fr: {
    nav: {
      home: "Accueil",
      about: "À propos",
      products: "Produits",
      industries: "Industries",
      sustainability: "Durabilité",
      news: "Actualités",
      careers: "Carrières",
      contact: "Contact",
      getQuote: "Demander un devis",
    },
    common: {
      learnMore: "En savoir plus",
      exploreProducts: "Voir les produits",
      contactUs: "Nous contacter",
      readMore: "Lire la suite",
      viewAll: "Tout voir",
      send: "Envoyer",
      sending: "Envoi...",
    },
    home: {
      heroOverline: "Produits chimiques industriels · Solutions minières",
      heroTitle: "Accélérer la croissance industrielle de l'Afrique grâce à une chimie innovante et durable.",
      heroSub:
        "Masika Chem SARL fournit les produits chimiques et pièces de rechange essentiels à l'industrie minière du Grand Katanga — le bon produit, au juste prix, au bon moment.",
      ctaPrimary: "Découvrir nos produits",
      ctaSecondary: "Parler à notre équipe",
      trustedBy: "La confiance des grands opérateurs miniers de la RDC",
      stat1: "30+", stat1Label: "Produits spécialisés",
      stat2: "8+", stat2Label: "Grands partenaires miniers",
      stat3: "24/7", stat3Label: "Logistique & support",
      stat4: "100%", stat4Label: "Conforme ARSP",
      industriesOverline: "Industries desservies",
      industriesTitle: "Une chimie sur mesure pour chaque étape du traitement minéral.",
      industriesSub:
        "De la comminution au raffinage, nos réactifs et consommables maintiennent les chaînes de production des opérations cuivre, cobalt, or et traitement d'eaux industrielles.",
      copper: "Cuivre",
      copperDesc: "Réactifs et corps broyants adaptés aux circuits cuivre sulfurés et oxydés.",
      cobalt: "Cobalt",
      cobaltDesc: "Chimie de flottation et lixiviation de précision pour une récupération optimale.",
      gold: "Or",
      goldDesc: "Cyanuration, CIP et produits de raffinage pour les opérations aurifères.",
      water: "Eaux industrielles",
      waterDesc: "Floculants, coagulants et chimie pH pour eaux de procédé et effluents.",
      productsOverline: "Catalogue produits",
      productsTitle: "Six catégories. Une chaîne d'approvisionnement fiable.",
      productsSub:
        "Un portefeuille complet couvrant toute la chaîne de valeur du traitement minéral. Fiches techniques et conditionnements sur demande.",
      ctaBlockTitle: "Prêt à optimiser votre circuit de traitement ?",
      ctaBlockSub:
        "Parlez à notre équipe de Lubumbashi pour la fourniture de réactifs, le support technique et les services après-vente adaptés à votre opération.",
      ctaBlockBtn: "Demander une consultation",
    },
    about: {
      overline: "À propos",
      title: "Un partenaire katangais pour les opérations minières de classe mondiale.",
      sub:
        "Masika Chem SARL est née pour garantir la continuité de production. Nous combinons une présence locale à Lubumbashi à une chaîne d'approvisionnement rigoureuse, livrant des produits chimiques et pièces conformes ARSP aux acteurs de la croissance industrielle africaine.",
      missionTitle: "Notre mission",
      missionBody:
        "Apporter des solutions innovantes et durables à la croissance industrielle de l'Afrique — en livrant le bon produit, au juste prix, au bon moment.",
      visionTitle: "Notre vision",
      visionBody:
        "Devenir le partenaire chimique industriel le plus fiable du secteur minier africain, reconnu pour son expertise technique, son intégrité et la continuité de son approvisionnement.",
      valuesTitle: "Nos valeurs",
      v1: "Fiabilité",
      v1d: "Un approvisionnement continu pour que la production de nos clients ne s'arrête jamais.",
      v2: "Qualité",
      v2d: "Des réactifs certifiés conformes aux exigences ARSP et opérateurs.",
      v3: "Expertise locale",
      v3d: "Une connaissance approfondie des mines katangaises, de la logistique et de la réglementation.",
      v4: "Durabilité",
      v4d: "Une chimie responsable au service d'un développement industriel durable.",
    },
    products: {
      overline: "Catalogue produits",
      title: "Produits chimiques et pièces de rechange pour l'industrie minière.",
      sub:
        "Organisés par étape du circuit de traitement minéral. Conditionnements sur mesure et fourniture en vrac disponibles. Contactez-nous pour les fiches techniques.",
      tableProduct: "Produit",
      tableFormula: "Formule / Qualité",
      tableApp: "Application principale",
      tableCta: "Devis",
      categories: {
        size_reduction: "Réduction granulométrique",
        separation: "Séparation",
        flotation: "Flottation",
        dewatering: "Déshydratation",
        refining: "Raffinage",
        spares: "Corps broyants & pièces",
      },
    },
    industries: {
      overline: "Industries",
      title: "Conçu pour les réalités de l'industrie minière africaine.",
      sub:
        "Chaque minéral a sa chimie. Nous accompagnons les opérations cuivre, cobalt, or et traitement d'eaux avec des programmes de réactifs adaptés aux gisements et conditions locales.",
    },
    sustainability: {
      overline: "Durabilité & HSE",
      title: "Une chimie sûre. Un approvisionnement responsable.",
      sub:
        "Santé, sécurité et environnement sont au cœur de notre façon de sourcer, transporter et livrer les produits chimiques sur la Copperbelt.",
      p1Title: "Santé & Sécurité",
      p1Body:
        "Conformité aux normes internationales de manipulation et transport des matières dangereuses, avec FDS pour chaque produit.",
      p2Title: "Environnement",
      p2Body:
        "Nous aidons les opérateurs à optimiser le dosage des réactifs — réduire l'empreinte chimique sans compromettre la récupération.",
      p3Title: "Gouvernance",
      p3Body:
        "Approvisionnement conforme ARSP et chaînes d'approvisionnement traçables, du fabricant à la mine.",
    },
    news: {
      overline: "Actualités & analyses",
      title: "Nouvelles de la Copperbelt.",
      sub: "Mises à jour sectorielles, notes techniques et annonces de l'équipe Masika Chem.",
      items: [
        {
          tag: "Opérations",
          title: "Extension des entrepôts à Lubumbashi pour soutenir un approvisionnement 24/7",
          excerpt: "Une capacité de stockage accrue permet une rotation plus rapide de l'acide sulfurique, des xanthates et des corps broyants au Katanga.",
          date: "Nov. 2025",
        },
        {
          tag: "Technique",
          title: "Optimiser le dosage des collecteurs dans les circuits de flottation cuivre oxydé",
          excerpt: "Note technique sur l'ajustement des mélanges PAX/SIBX pour les minerais mixtes sulfurés-oxydés courants en RDC.",
          date: "Oct. 2025",
        },
        {
          tag: "Partenariat",
          title: "Renouvellement d'un accord pluriannuel avec un grand producteur katangais",
          excerpt: "Masika Chem prolonge son partenariat de fourniture de réactifs et pièces avec un opérateur cuivre-cobalt majeur de la région.",
          date: "Sep. 2025",
        },
      ],
    },
    careers: {
      overline: "Carrières",
      title: "Construire la colonne vertébrale industrielle de l'Afrique avec nous.",
      sub:
        "Nous sommes une équipe ambitieuse basée à Lubumbashi. Si vous croyez en la fiabilité, la chimie et l'excellence opérationnelle, nous serions ravis de vous rencontrer.",
      openTitle: "Postes ouverts",
      none: "Aucun recrutement actif pour le moment — nous restons ouverts aux candidatures spontanées.",
      role1: "Ingénieur Commercial — Réactifs Miniers",
      role1Loc: "Lubumbashi, RDC",
      role2: "Coordinateur Logistique",
      role2Loc: "Lubumbashi, RDC",
      role3: "Représentant Technico-Commercial — Kolwezi",
      role3Loc: "Kolwezi, RDC",
      apply: "Postuler par e-mail",
    },
    contact: {
      overline: "Contact",
      title: "Parlez à notre équipe à Lubumbashi.",
      sub:
        "Contactez-nous pour des spécifications produits, devis ou support technique. Réponse sous un jour ouvré.",
      formName: "Nom complet",
      formCompany: "Entreprise",
      formEmail: "E-mail professionnel",
      formPhone: "Téléphone (optionnel)",
      formMessage: "Comment pouvons-nous vous aider ?",
      formSubmit: "Envoyer",
      success: "Merci. Votre message a bien été reçu — nous reviendrons vers vous rapidement.",
      error: "Une erreur est survenue. Merci de réessayer ou de nous écrire directement.",
      address: "Adresse",
      addressValue: "N° 44 Avenue Sendwe, Lubumbashi, R.D. Congo",
      phone: "Téléphone",
      email: "E-mail",
      hours: "Horaires",
      hoursValue: "Lundi – Vendredi · 08h00 – 17h00 (CAT)",
    },
    footer: {
      tagline:
        "Produits chimiques industriels et pièces de rechange pour les opérations minières du Grand Katanga.",
      navTitle: "Navigation",
      contactTitle: "Contact",
      legalTitle: "Mentions légales",
      privacy: "Politique de confidentialité",
      terms: "Conditions d'utilisation",
      rights: "Tous droits réservés.",
    },
  },
};
